import type { TFieldErrors } from "@/utils/serverErrors";
import type { GraphQLFormattedError } from "graphql";

import { ApolloLink, Observable } from "@apollo/client";

import i18n from "@/i18n";
import { notificationService } from "@/services/notificationService";

import {
  createErrorHandlers,
  redirectToLogin,
} from "../handlers/errorHandlers";

export interface CustomGraphQLFormattedError extends GraphQLFormattedError {
  messageKey?: string;
  code?: number;
  fields?: TFieldErrors;
}

const OPERATIONS_WITH_PERSISTENT_NETWORK_ERROR_TOAST = ["ImportShortLinkCsv"];

const logGraphQLError = (
  error: CustomGraphQLFormattedError,
  operationName?: string
) => {
  const { message, messageKey, code } = error;
  console.error(
    `[GraphQL error]: Message: ${message}, MessageKey: ${messageKey}, Code: ${code}, Operation: ${operationName}`
  );
};

const handleGraphQLErrors = (
  errors: readonly CustomGraphQLFormattedError[],
  operationName?: string,
  isLocalError?: boolean
) => {
  const errorHandlers = createErrorHandlers();
  const handledCategories = new Set<string>();

  errors.forEach((error) => {
    logGraphQLError(error, operationName);
    const handler = errorHandlers.find(
      (h) => h.messageKey && h.messageKey === error.messageKey
    );

    if (handler) {
      handledCategories.add(error.messageKey || "handled");
      handler.handle(operationName);
    }
  });

  if (handledCategories.size === 0 && !isLocalError) {
    notificationService.danger({ msg: i18n.t("notification.processingError") });
  }
};

const handleNetworkError = (statusCode?: number, isAutoClose = true) => {
  console.error(`[Network error]: Status ${statusCode || "unknown"}`);
  if (!statusCode) {
    notificationService.danger({
      msg: i18n.t("notification.networkError"),
      isAutoClose,
    });
    return;
  }
  if (statusCode === 401) redirectToLogin();
  else if (statusCode >= 500)
    notificationService.danger({
      msg: i18n.t("notification.internalServerError"),
      isAutoClose,
    });
  else if (statusCode >= 400)
    notificationService.danger({
      msg: i18n.t("notification.clientError"),
      isAutoClose,
    });
};

export const errorLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const context = operation.getContext();
    const isLocalError = context.errorType === "local";
    const shouldKeepToastOpen =
      operation.operationName &&
      OPERATIONS_WITH_PERSISTENT_NETWORK_ERROR_TOAST.includes(
        operation.operationName
      );

    const subscription = forward(operation).subscribe({
      next: (result) => {
        if (result.errors)
          handleGraphQLErrors(
            result.errors,
            operation.operationName,
            isLocalError
          );
        const httpResponse = context.response;
        if (httpResponse && httpResponse.status >= 400)
          handleNetworkError(httpResponse.status, !shouldKeepToastOpen);
        observer.next(result);
      },
      error: (networkError) => {
        const statusCode =
          "statusCode" in networkError ? networkError.statusCode : undefined;

        handleNetworkError(
          statusCode as number | undefined,
          !shouldKeepToastOpen
        );
        observer.error(networkError);
      },
      complete: () => observer.complete(),
    });
    return () => subscription.unsubscribe();
  });
});
